<template>
  <div class="ec-page">

    <div class="ec-header">
      <span class="ec-back" @click="viewClose('someStatus')">
        <img :src="downArrow" alt="Back" />
      </span>
      <h1>Event Center</h1>
      <span class="ec-headerSpacer"></span>
    </div>

    <div class="ec-inner">

      <!-- Card shapes rather than a spinner, so the list does not jump
           when the events land. -->
      <div class="ec-list" v-if="loading">
        <div v-for="n in 3" :key="n" class="ec-card">
          <span class="sk ec-thumb"></span>
          <div class="ec-skelBody">
            <span class="sk"></span>
            <span class="sk"></span>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="ec-card">
        <p class="ec-state">
          {{ error }}
          <br />
          <button type="button" class="ec-retry" @click="fetchEvents">Try again</button>
        </p>
      </div>

      <div v-else-if="!events.length" class="ec-card">
        <p class="ec-state">No events are running right now.<br />Check back soon.</p>
      </div>

      <div class="ec-list" v-else>
        <button v-for="(event, i) in events" :key="event.id" type="button" class="ec-card"
          @click="openEvent(event)">
          <span class="ec-thumb">
            <!-- The first banner is what the reader sees immediately, so it
                 loads eagerly at high priority; the rest wait until they are
                 near the viewport. -->
            <img :src="event.image" :alt="event.name" width="840" height="229" decoding="async"
              :loading="i === 0 ? 'eager' : 'lazy'" :fetchpriority="i === 0 ? 'high' : 'auto'"
              @error="onImageError" />
            <span class="ec-new" v-if="event.isNew">New</span>
          </span>

          <span class="ec-body">
            <span class="ec-text">
              <span class="ec-name">{{ event.name }}</span>
              <span class="ec-meta">
                <span>Long-term event</span>
                <span class="ec-dot" v-if="event.updatedLabel"></span>
                <span v-if="event.updatedLabel">Updated {{ event.updatedLabel }}</span>
              </span>
            </span>
            <svg class="ec-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9.5 5l7 7-7 7" />
            </svg>
          </span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { viewClose as androidViewClose } from '@/utils/android';
import axios from "axios";
import "@/assets/css/skeleton.css";
import "./EventCenter.css";
import "./EventCenterRefresh.css";
import downArrow from "../../assets/images/back-blackArrow.svg";

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
  name: "EventCenter",

  data() {
    return {
      loading: true,
      error: "",
      events: [],
      downArrow,
      token: "",
    };
  },

  mounted() {
    this.fetchEvents();
  },

  methods: {
    fetchEvents() {
      this.loading = true;
      this.error = "";

      const tokenParam = this.$route.params.token || "";
      const token = tokenParam.includes("=") ? tokenParam.split("=").pop() : tokenParam;
      this.token = token;

      axios
        .get(`/api/getbannerList?type=2`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const rows = Array.isArray(response?.data?.result) ? response.data.result : [];
          // Derive once here rather than calling helpers from the template on
          // every re-render.
          this.events = rows.map((row) => ({
            ...row,
            isNew: this.isNewEvent(row.updated_at),
            updatedLabel: this.formatDate(row.updated_at),
          }));
        })
        .catch((error) => {
          console.error("Error Fetching Data", error);
          this.error = "Could not load the events. Check your connection and try again.";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Open the event url, carrying the token through
    openEvent(event) {
      if (!event?.url) return;

      const token = this.token;
      let finalUrl = "";

      // case 1: API already gives "...token="
      if (event.url.includes("token=")) {
        finalUrl = `${event.url}${token}`;
      }
      // case 2: url already has query params
      else if (event.url.includes("?")) {
        finalUrl = `${event.url}&token=${token}`;
      }
      // case 3: clean url
      else {
        finalUrl = `${event.url}?token=${token}`;
      }

      window.location.href = finalUrl;
    },

    // A banner that 404s used to leave a grey box with no explanation; hiding
    // the image lets the card fall back to its own placeholder.
    onImageError(e) {
      if (e?.target) e.target.style.visibility = "hidden";
    },

    // Flagged as new when it was touched within the last 30 days
    isNewEvent(updatedAt) {
      if (!updatedAt) return false;

      const updated = new Date(updatedAt);
      if (isNaN(updated.getTime())) return false;

      const diffDays = (Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24);
      return diffDays <= 30;
    },

    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";
      return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
    },

    // Close the WebView on back arrow click
    viewClose(status) {
      if (window.Android && typeof window.Android.viewClose === "function") {
        androidViewClose(status);
      } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.viewClose) {
        window.webkit.messageHandlers.viewClose.postMessage("close");
      } else {
        window.parent.postMessage({ action: "closePopup", status }, "*");
      }
    },
  },
};
</script>
